'use client';

import { useRef, useState } from 'react';
import { Content } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { SliceComponentProps } from '@prismicio/react';
import { Badge, Button, formatPrice } from '@get-mosh/msh-shared';
import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import styles from '../../src/views/Home/home.module.scss';
import { trackClick } from 'src/analytics/Events';
import EventNames from 'src/analytics/EventNames';
import { backgroundColourVariants } from 'src/constants/style';
import {
    HeroProductv2SliceDefaultPrimaryCarouselItem,
    HeroProductv2SliceDefaultPrimarySellingPointsItem,
} from 'prismicio-types';
import type { Product, WithContext } from 'schema-dts';

/**
 * Props for `HeroProductv2`.
 */
export type HeroProductv2Props =
    SliceComponentProps<Content.HeroProductv2Slice> & {
        context: {
            pageType: string;
        };
    };

/**
 * Component for "HeroProductv2" Slices.
 */
const HeroProductv2 = ({ slice, context }: HeroProductv2Props): JSX.Element => {
    const [currentHeroImage, setCurrentHeroImage] = useState(
        slice.primary.carousel ? slice.primary?.carousel[0]?.image : null,
    );
    const carouselRef = useRef<HTMLDivElement>(null);

    const structuredData: WithContext<Product> = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: slice.primary.title || undefined,
        image: slice.primary.carousel.map(
            (item: HeroProductv2SliceDefaultPrimaryCarouselItem) =>
                item.image.url ?? '',
        ),
        description: slice.primary.description ?? undefined,
        brand: {
            '@type': 'Brand',
            name: 'Mosh',
        },
        offers: {
            '@type': 'Offer',
            price: slice.primary.original_price?.toFixed(2) ?? undefined,
            priceCurrency: 'AUD',
            availability: 'https://schema.org/InStock',
        },
    };

    return (
        <>
            <script
                key="structured-data"
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(structuredData),
                }}
            />
            <section
                data-slice-type={slice.slice_type}
                data-slice-variation={slice.variation}
                className={classNames(
                    'px-4 py-8 lg:py-16',
                    backgroundColourVariants[slice.primary.background],
                )}
            >
                {/* Mobile */}
                <div className="container relative flex flex-col bg-white px-4 py-8 shadow-md md:hidden">
                    <div
                        id="carousel-container"
                        className="flex w-full overflow-x-auto rounded-lg pb-4"
                        ref={carouselRef}
                        style={{ scrollSnapType: 'x mandatory' }}
                    >
                        {slice.primary.carousel.map(
                            (
                                item: HeroProductv2SliceDefaultPrimaryCarouselItem,
                                i: number,
                            ) => (
                                <div
                                    key={i}
                                    className="relative w-[100%] flex-shrink-0 snap-start"
                                >
                                    <PrismicNextImage
                                        field={item.image}
                                        className="aspect-square object-cover"
                                    />
                                </div>
                            ),
                        )}

                        {slice.primary.badge_text ? (
                            <div className="absolute right-[38px] top-[40px] flex items-center justify-center gap-2 rounded-full bg-purple-extra-light px-3 py-2 text-purple-dark">
                                <FontAwesomeIcon
                                    className="h-5 w-5 rounded-full text-purple-dark"
                                    icon={faStar}
                                />
                                <span className="text-sm font-semibold text-purple-dark">
                                    {slice.primary.badge_text}
                                </span>
                            </div>
                        ) : null}
                    </div>

                    <div className="flex flex-col gap-2">
                        <div>
                            <h1 className="font-demi text-[18px] text-grey-400">
                                {slice.primary.seo_heading}
                            </h1>
                        </div>

                        <div>
                            <h3 className="font-demi">{slice.primary.title}</h3>
                        </div>

                        <div className="flex flex-col items-start gap-2">
                            {slice.primary.original_price &&
                            slice.primary.original_price !==
                                slice.primary.price ? (
                                <p className="text-grey-400 line-through">
                                    {`From ${formatPrice(
                                        slice.primary.original_price as number,
                                    )}`}
                                    {slice.primary.duration}
                                </p>
                            ) : null}
                            {slice.primary.highlight_price === false ? (
                                <h4 className="text-blue">
                                    {`From ${formatPrice(
                                        slice.primary.price as number,
                                    )}`}
                                    {slice.primary.duration}
                                </h4>
                            ) : (
                                <span className="inline-flex items-baseline gap-1">
                                    <h3 className="font-bold">
                                        {`From ${formatPrice(
                                            slice.primary.price as number,
                                        )}`}
                                    </h3>
                                    <p className="p2">
                                        {slice.primary.duration}
                                    </p>
                                </span>
                            )}
                            {slice.primary.price_badge ? (
                                <Badge variant="fill-dark" rounded={false}>
                                    {slice.primary.price_badge}
                                </Badge>
                            ) : null}
                        </div>
                    </div>

                    <div className="my-6 flex">
                        <Button
                            text={slice.primary.button_text || 'Learn more'}
                            as="a"
                            href={slice.primary.link || '/'}
                            onClick={() => {
                                trackClick(EventNames.BUTTON_CLICKED, {
                                    label:
                                        slice.primary.button_text ||
                                        'Learn more',
                                    link: slice.primary.link || '/',
                                    variation: slice.variation,
                                    location: slice.slice_type,
                                });
                            }}
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        {slice.primary.description ? (
                            <div>
                                <p className="text-grey-400">
                                    {slice.primary.description}
                                </p>
                            </div>
                        ) : null}

                        {slice.primary.selling_points.map(
                            (
                                item: HeroProductv2SliceDefaultPrimarySellingPointsItem,
                                index: number,
                            ) => (
                                <div
                                    key={index}
                                    className={classNames(
                                        'flex w-full flex-col gap-2',
                                        { 'pt-2': (index = 0) },
                                    )}
                                >
                                    <div className="flex flex-col gap-2">
                                        <p className="font-semibold ">
                                            {item.title}
                                        </p>
                                        <p className="my-0 text-grey-400">
                                            {item.description}
                                        </p>
                                    </div>
                                    {index !==
                                    slice.primary.selling_points.length - 1 ? (
                                        <div>
                                            <hr style={{ color: '#e2e2e2' }} />
                                        </div>
                                    ) : null}
                                </div>
                            ),
                        )}
                    </div>
                </div>

                {/* Desktop */}
                <div className="hidden justify-center md:flex">
                    <div className="container flex flex-wrap justify-center gap-8 rounded-2xl bg-white p-4 shadow-md md:flex-nowrap">
                        {/* Left Content */}
                        <div className="flex flex-1 flex-col gap-4">
                            <div className="relative flex items-center self-stretch">
                                <PrismicNextImage
                                    field={currentHeroImage}
                                    className="aspect-square rounded-lg object-cover"
                                />
                                {slice.primary.badge_text ? (
                                    <div className="absolute right-[35px] top-[16px] flex items-center justify-center gap-2 rounded-full bg-purple-extra-light px-3 py-2 text-purple-dark">
                                        <FontAwesomeIcon
                                            className="h-5 w-5 rounded-full"
                                            icon={faStar}
                                        />
                                        <span className="text-sm font-semibold">
                                            {slice.primary.badge_text}
                                        </span>
                                    </div>
                                ) : null}
                            </div>
                            <div
                                className={classNames(
                                    styles['hero-tiles'],
                                    styles['hero-tiles--carousel'],
                                    'hide-scrollbar flex h-[80px] gap-4 overflow-x-auto',
                                )}
                                data-hero-cta-tiles
                            >
                                {slice.primary.carousel.length > 1
                                    ? slice.primary.carousel.map(
                                          (
                                              item: HeroProductv2SliceDefaultPrimaryCarouselItem,
                                              i: number,
                                          ) => (
                                              <div
                                                  key={i}
                                                  onClick={() =>
                                                      setCurrentHeroImage(
                                                          item.image,
                                                      )
                                                  }
                                                  className="h-20 w-20 flex-shrink-0"
                                              >
                                                  <PrismicNextImage
                                                      field={item.image}
                                                      className={classNames(
                                                          'aspect-square max-h-full rounded-lg object-cover',
                                                          {
                                                              'border-2 border-purple':
                                                                  item.image ===
                                                                  currentHeroImage,
                                                          },
                                                      )}
                                                  />
                                              </div>
                                          ),
                                      )
                                    : null}
                            </div>
                        </div>

                        {/* Right Content */}
                        <div className="flex min-w-0 flex-1 flex-col md:flex-wrap">
                            <div className="flex flex-col gap-2 pb-6">
                                <h1 className="font-demi text-[18px] text-grey-400">
                                    {slice.primary.seo_heading}
                                </h1>
                                <h3 className="font-demi">
                                    {slice.primary.title}
                                </h3>
                                {slice.primary.highlight_price === false ? (
                                    <>
                                        {slice.primary.original_price &&
                                        slice.primary.original_price !==
                                            slice.primary.price ? (
                                            <p className="text-grey-400 line-through">
                                                {`From ${formatPrice(
                                                    slice.primary
                                                        .original_price as number,
                                                )}`}
                                                {slice.primary.duration}
                                            </p>
                                        ) : null}
                                        <div className="flex flex-row items-center gap-2">
                                            <h4 className="text-blue">
                                                {`From ${formatPrice(
                                                    slice.primary
                                                        .price as number,
                                                )}${slice.primary.duration}`}
                                            </h4>
                                            {slice.primary.price_badge ? (
                                                <Badge
                                                    variant="fill-dark"
                                                    rounded={false}
                                                >
                                                    {slice.primary.price_badge}
                                                </Badge>
                                            ) : null}
                                        </div>
                                    </>
                                ) : null}
                            </div>

                            <div className="flex flex-col gap-2">
                                {slice.primary.description ? (
                                    <div>{slice.primary.description}</div>
                                ) : null}

                                {slice.primary.selling_points.map(
                                    (
                                        item: HeroProductv2SliceDefaultPrimarySellingPointsItem,
                                        index: number,
                                    ) => (
                                        <div
                                            key={index}
                                            className={classNames(
                                                'flex w-full flex-col gap-2',
                                                { 'pt-2': index === 0 },
                                            )}
                                        >
                                            <div className="flex flex-col gap-2">
                                                <p className="font-semibold">
                                                    {item.title}
                                                </p>
                                                <p className="my-0 text-grey-400">
                                                    {item.description}
                                                </p>
                                            </div>
                                            {index !==
                                            slice.primary.selling_points
                                                .length -
                                                1 ? (
                                                <div>
                                                    <hr
                                                        style={{
                                                            color: '#e2e2e2',
                                                        }}
                                                    />
                                                </div>
                                            ) : null}
                                        </div>
                                    ),
                                )}
                            </div>

                            <div className="mt-8 flex flex-col gap-6">
                                {slice.primary.highlight_price === true ? (
                                    <span className="inline-flex items-baseline gap-1">
                                        <h3 className="font-bold">
                                            {`From ${formatPrice(
                                                slice.primary.price as number,
                                            )}`}
                                        </h3>
                                        <p className="p2">
                                            {slice.primary.duration}
                                        </p>
                                    </span>
                                ) : null}
                                <Button
                                    text={
                                        slice.primary.button_text ||
                                        'Learn more'
                                    }
                                    as="a"
                                    href={slice.primary.link || '/'}
                                    onClick={() => {
                                        trackClick(EventNames.BUTTON_CLICKED, {
                                            label:
                                                slice.primary.button_text ||
                                                'Learn more',
                                            link: slice.primary.link || '/',
                                            variation: slice.variation,
                                            location: slice.slice_type,
                                        });
                                    }}
                                    stretch
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default HeroProductv2;
